import { useEffect, useState, useCallback, useRef } from "react";
import { API } from "./api";

// Polls the counter every `POLL_MS` so all visitors see live updates without refresh.
const POLL_MS = 15000;

export const useCounter = () => {
  const [count, setCount] = useState(786);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/counter`, { cache: "no-store" });
      const data = await res.json();
      if (mounted.current && typeof data.count === "number") setCount(data.count);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("Counter fetch failed", e);
    } finally { if (mounted.current) setLoading(false); }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const interval = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      mounted.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  const increment = useCallback(async () => {
    try {
      const res = await fetch(`${API}/counter/increment`, { method: "POST" });
      const data = await res.json();
      if (res.ok && typeof data.count === "number") {
        setCount(data.count);
        return { ok: true, count: data.count };
      }
      if (res.status === 429) {
        const fallback = data?.detail?.count;
        if (typeof fallback === "number") setCount(fallback);
        return { ok: false, rateLimited: true, count: fallback || count };
      }
      return { ok: false, count };
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("Counter increment failed", e);
      return { ok: false, count };
    }
  }, [count]);

  return { count, loading, refresh, increment };
};
